import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTenantSafe } from '../context/TenantContext';
import HarvestForm from '../components/harvesting/HarvestForm';
import { HarvestRecordEditDialog } from '../components/harvesting/HarvestRecordEditor';
import GuidedHarvestForm from '../components/harvesting/GuidedHarvestForm';
import AssistedParticipantPanel from '../components/harvesting/AssistedParticipantPanel';
import { canStaffSection } from '../utils/staffAccess';
import { resolveHarvestCategory } from '../components/harvesting/harvestFlow';
import HarvestMap  from '../components/harvesting/HarvestMap';
import MapEngine   from '../components/harvesting/MapEngine';
import MapLayerControls, { configuredManagementLayers, isLakesLayer } from '../components/harvesting/MapLayerControls';
import SpeciesAutocomplete from '../components/harvesting/SpeciesAutocomplete';
import { DashTile, DashSection, DashSegBar, DB_STYLE, downloadFile, FilterChipBar } from '../components/dashboard/DashKit';
import { useUrlParams } from '../hooks/useUrlParams';
import {
  getStats,
  getRecords,
  getMyRecords,
  submitRecord,
  submitGuidedHarvest,
  submitAssistedGuidedHarvest,
  setRecordFlags,
  addRecordNote,
  deleteRecord,
  getHarvestConfig,
} from '../services/harvestingService';
import { getPolygonsForSpecies } from '../services/stewardshipService';
import { lookupGeographyPoint, getAvailableGeographyLayers, getManagedLayerGeoJson } from '../services/geographyService';

// ─── Shared styles ────────────────────────────────────────────────────────────
const S = {
  page:    { padding: '28px 32px', maxWidth: 1100, margin: '0 auto' },
  heading: { fontSize: 24, fontWeight: 700, marginBottom: 20 },
  tabs:    { display: 'flex', gap: 8, marginBottom: 24 },
  tab: (active) => ({
    padding: '8px 18px', borderRadius: 6, fontWeight: 600, fontSize: 14,
    border: active ? '2px solid #166534' : '2px solid #e5e7eb',
    background: active ? '#f0fdf4' : '#fff',
    color: active ? '#166534' : '#6b7280',
    cursor: 'pointer',
  }),
  card: {
    background: '#fff', border: '1px solid #e5e7eb',
    borderRadius: 10, padding: '18px 20px', marginBottom: 14,
  },
  badge: (color) => ({
    display: 'inline-block', padding: '2px 8px',
    borderRadius: 10, fontSize: 11, fontWeight: 700,
    background: color + '22', color: color,
  }),
  btn: (bg = '#1d4ed8', fg = '#fff') => ({
    padding: '7px 16px', background: bg, color: fg,
    border: 'none', borderRadius: 6, cursor: 'pointer',
    fontSize: 13, fontWeight: 600,
  }),
  err: {
    background: '#fef2f2', border: '1px solid #fca5a5',
    borderRadius: 6, padding: '10px 14px', color: '#991b1b', fontSize: 14, marginBottom: 14,
  },
  stat: {
    background: '#f9fafb', borderRadius: 8, padding: '14px 18px',
    border: '1px solid #e5e7eb', textAlign: 'center',
  },
};

const CATEGORY_LABEL = {
  plants_medicine: '🌿 Plants & Medicine',
  hunting:         '🦌 Hunting',
  birds:           '🦅 Birds',
  fishing:         '🎣 Fishing',
  environmental:   '⚠️ Environmental',
  other:           '📍 Other',
};
const SEASON_LABEL = { spring: '🌸 Spring', summer: '☀️ Summer', fall: '🍂 Fall', winter: '❄️ Winter' };

// Staff-only triage flags. Never shown to the contributor who submitted the record.
const FLAG_META = {
  suspicious: { label: '⚠️ Suspicious', color: '#f59e0b' },
  bot:        { label: '🤖 Bot',        color: '#ef4444' },
  duplicate:  { label: '⧉ Duplicate',   color: '#a855f7' },
  other:      { label: '🚩 Other',      color: '#64748b' },
  data_impaired: { label: 'Data impaired', color: '#f59e0b' },
  pipeline_excluded: { label: 'Exclude from pipelines', color: '#ef4444' },
};
const FLAG_TYPES = Object.keys(FLAG_META);

// ─── Helpers ─────────────────────────────────────────────────────────────────
function StatGrid({ items, keyLabel, countLabel = 'Count' }) {
  if (!items?.length) return <p style={{ color: '#6b7280' }}>No data yet.</p>;
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(140px, 1fr))', gap: 10, marginBottom: 16 }}>
      {items.map((i) => (
        <div key={i._id} style={S.stat}>
          <div style={{ fontSize: 22, fontWeight: 700 }}>{i.count}</div>
          <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>
            {CATEGORY_LABEL[i._id] || SEASON_LABEL[i._id] || i._id || '(unknown)'}
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── FlagsNotesPanel ──────────────────────────────────────────────────────────
// Staff-only triage panel: toggle flags and append internal notes. Neither is
// ever returned to the contributor by the backend.
function FlagsNotesPanel({ record, onSetFlags, onAddNote }) {
  const [flags, setFlags]           = useState(record.flags || []);
  const [noteText, setNoteText]     = useState('');
  const [savingFlags, setSavingFlags] = useState(false);
  const [savingNote, setSavingNote]   = useState(false);
  const [error, setError] = useState('');

  const toggleFlag = (f) => {
    setFlags((prev) => (prev.includes(f) ? prev.filter((x) => x !== f) : [...prev, f]));
  };

  const saveFlags = async () => {
    setSavingFlags(true);
    setError('');
    try { await onSetFlags(record._id, flags); }
    catch (err) { setError(err.response?.data?.message || 'Could not save quality flags. Retry before relying on pipeline exclusion.'); }
    finally { setSavingFlags(false); }
  };

  const saveNote = async () => {
    if (!noteText.trim()) return;
    setSavingNote(true);
    setError('');
    try { await onAddNote(record._id, noteText.trim()); setNoteText(''); }
    catch (err) { setError(err.response?.data?.message || 'Could not save the impairment note.'); }
    finally { setSavingNote(false); }
  };

  return (
    <div style={{ marginTop: 14, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, padding: '14px 16px' }}>
      <div style={{ fontWeight: 600, marginBottom: 10 }}>
        Flags &amp; Internal Notes{' '}
        <span style={{ fontWeight: 400, fontSize: 12, color: '#94a3b8' }}>(staff only — never shown to the contributor)</span>
      </div>
      <p>Contributors own their data; submissions do not need approval. Any saved flag excludes this record from new lens, aggregate, planning and stewardship-evidence queries, without removing it from the contributor's records. Use internal notes to explain the impairment or exclusion. Removing all flags restores eligibility, not a certification that the data is correct.</p>
      {error && <p role="alert">{error}</p>}

      <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center', marginBottom: 12 }}>
        {FLAG_TYPES.map((f) => (
          <label key={f} style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 13 }}>
            <input type="checkbox" checked={flags.includes(f)} onChange={() => toggleFlag(f)} />
            {FLAG_META[f].label}
          </label>
        ))}
        <button style={S.btn('#f3f4f6', '#374151')} onClick={saveFlags} disabled={savingFlags}>
          {savingFlags ? 'Saving…' : 'Save Flags'}
        </button>
      </div>

      {record.internalNotes?.length > 0 && (
        <div style={{ marginBottom: 10, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {record.internalNotes.map((n, i) => (
            <div key={i} style={{ background: '#fefce8', padding: '8px 12px', borderRadius: 6, fontSize: 13 }}>
              <div>{n.text}</div>
              <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 4 }}>
                {n.authorId?.name || 'Staff'} · {n.createdAt ? new Date(n.createdAt).toLocaleString() : ''}
              </div>
            </div>
          ))}
        </div>
      )}

      <textarea
        value={noteText}
        onChange={(e) => setNoteText(e.target.value)}
        placeholder="Add an internal note (e.g. context from a conversation with the harvester)…"
        rows={2}
        style={{
          width: '100%', padding: '8px 10px', border: '1px solid #d1d5db',
          borderRadius: 6, fontSize: 13, boxSizing: 'border-box', resize: 'vertical',
        }}
      />
      <div style={{ display: 'flex', gap: 8, marginTop: 8 }}>
        <button style={S.btn()} onClick={saveNote} disabled={savingNote || !noteText.trim()}>
          {savingNote ? 'Adding…' : 'Add Note'}
        </button>
      </div>
    </div>
  );
}

// ─── RecordCard ───────────────────────────────────────────────────────────────
function RecordCard({ record, onSetFlags, onAddNote, onDelete, canReview, isOwn }) {
  const [expanded, setExpanded] = useState(false);
  const [panelOpen, setPanelOpen] = useState(false);

  const lat = record.location?.coordinates?.[1];
  const lng = record.location?.coordinates?.[0];
  const hasCoords = lat != null && lng != null;
  const flagCount = record.flags?.length || 0;

  return (
    <div style={S.card}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: 8 }}>
        <div>
          <span style={{ fontWeight: 700, fontSize: 16 }}>
            {CATEGORY_LABEL[record.category] || record.category}
          </span>
          {record.subcategory && (
            <span style={{ color: '#6b7280', marginLeft: 8 }}>— {record.subcategory}</span>
          )}
          {record.species && (
            <span style={{ color: '#374151', marginLeft: 8, fontStyle: 'italic' }}>{record.species}</span>
          )}
          {canReview && flagCount > 0 && record.flags.map((f) => (
            <span key={f} style={S.badge(FLAG_META[f]?.color || '#64748b')}>{FLAG_META[f]?.label || f}</span>
          ))}
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <button style={S.btn('#f3f4f6', '#374151')} onClick={() => setExpanded(!expanded)}>
            {expanded ? '▲ Less' : '▼ More'}
          </button>
          {canReview && (
            <button
              style={S.btn(flagCount > 0 ? '#fef2f2' : '#f3f4f6', flagCount > 0 ? '#dc2626' : '#374151')}
              onClick={() => setPanelOpen(!panelOpen)}
            >
              {flagCount > 0 ? `🚩 Flags & Notes (${flagCount})` : 'Flags & Notes'}
            </button>
          )}
          {(isOwn || canReview) && (
            <button
              style={S.btn('#ef4444')}
              onClick={() => { if (window.confirm('Delete this record?')) onDelete(record._id); }}
            >
              Delete
            </button>
          )}
        </div>
      </div>

      <div style={{ marginTop: 8, fontSize: 13, color: '#6b7280', display: 'flex', gap: 16, flexWrap: 'wrap' }}>
        <span>{SEASON_LABEL[record.season] || record.season}</span>
        <span>📅 {new Date(record.harvestDate).toLocaleDateString()}</span>
        {record.quantity != null && <span>{record.harvestDetails?.caughtQuantity != null ? 'Kept' : 'Qty'}: {record.quantity} {record.unit}</span>}
        {record.harvestDetails?.caughtQuantity != null && <span>Caught / harvested: {record.harvestDetails.caughtQuantity} {record.unit} · {record.harvestDetails.resourceOutcome.replace(/_/g, ' ')}</span>}
        {record.harvestDetails?.sizeValue != null && <span>Size: {record.harvestDetails.sizeValue} {record.harvestDetails.sizeUnit}</span>}
        {record.harvestDetails?.sex && <span>Sex: {record.harvestDetails.sex}</span>}
        {record.harvestDetails?.condition && <span>Condition: {record.harvestDetails.condition.replace(/_/g, ' ')}</span>}
        {record.abundance && <span>Abundance: {record.abundance.replace('_', ' ')}</span>}
        {hasCoords && <span>📍 {lat.toFixed(4)}, {lng.toFixed(4)}</span>}
        {record.userId?.name && <span>By: {record.userId.name}</span>}
      </div>

      {expanded && (
        <div style={{ marginTop: 12, fontSize: 14, color: '#374151' }}>
          {record.observations && (
            <p style={{ marginBottom: 8 }}>
              <strong>Observations:</strong> {record.observations}
            </p>
          )}
          {record.traditionalKnowledge && (
            <p style={{ marginBottom: 8 }}>
              <strong>Traditional knowledge:</strong> {record.traditionalKnowledge}
            </p>
          )}
          {record.isTraditionalUse && (
            <p style={{ color: '#166534' }}>
              ✓ Marked as traditional use site
            </p>
          )}
          {record.locationDescription && (
            <p style={{ marginBottom: 8 }}>
              <strong>Location notes:</strong> {record.locationDescription}
            </p>
          )}
        </div>
      )}

      {panelOpen && canReview && (
        <FlagsNotesPanel record={record} onSetFlags={onSetFlags} onAddNote={onAddNote} />
      )}
    </div>
  );
}

// ─── ReviewerView ─────────────────────────────────────────────────────────────
function ReviewerView({ tenantId }) {
  const [urlP, setUrlP] = useUrlParams({ tab: 'all', category: '', dateFrom: '', dateTo: '' });
  const tab            = ['all', 'flagged', 'map', 'stats', 'dashboard'].includes(urlP.tab) ? urlP.tab : 'all';
  const categoryFilter = urlP.category;
  const dashDateFrom   = urlP.dateFrom;
  const dashDateTo     = urlP.dateTo;
  const setTab             = (v) => setUrlP('tab',      v);
  const setCategoryFilter  = (v) => setUrlP('category', v);
  const setDashFrom        = (v) => setUrlP('dateFrom', v);
  const setDashTo          = (v) => setUrlP('dateTo',   v);
  const [stats, setStats]           = useState(null);
  const [records, setRecords]       = useState([]);
  const [loading, setLoading]       = useState(true);
  const [error, setError]           = useState('');

  const loadAll = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const [statsRes, recRes] = await Promise.all([
        getStats(tenantId),
        getRecords(tenantId, { limit: 100 }),
      ]);
      setStats(statsRes.data);
      setRecords(recRes.data.records);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load harvesting data');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { loadAll(); }, [loadAll]);

  const handleSetFlags = async (id, flags) => {
    await setRecordFlags(tenantId, id, flags);
    await loadAll();
  };

  const handleAddNote = async (id, text) => {
    await addRecordNote(tenantId, id, text);
    await loadAll();
  };

  const handleDelete = async (id) => {
    await deleteRecord(tenantId, id);
    await loadAll();
  };

  const allRecords = records.filter((r) => !categoryFilter || r.category === categoryFilter);
  const flagged     = allRecords.filter((r) => r.flags?.length > 0);

  if (loading) return <p>Loading…</p>;
  if (error)   return <div style={S.err}>{error}</div>;

  return (
    <div>
      {/* ── Quick stats summary ── */}
      {stats && (
        <div
          style={{
            display: 'grid', gridTemplateColumns: 'repeat(auto-fill,minmax(110px,1fr))',
            gap: 10, marginBottom: 24,
          }}
        >
          <div style={S.stat}>
            <div style={{ fontSize: 24, fontWeight: 700 }}>{stats.total}</div>
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>Total Records</div>
          </div>
          <div style={S.stat}>
            <div style={{ fontSize: 24, fontWeight: 700, color: stats.flagged > 0 ? '#dc2626' : undefined }}>{stats.flagged}</div>
            <div style={{ fontSize: 12, color: '#6b7280', marginTop: 2 }}>🚩 Flagged</div>
          </div>
        </div>
      )}

      {/* ── Drill-through chips: tab view + category filter ── */}
      {(tab !== 'all' || categoryFilter) && (() => {
        const TAB_META = {
          flagged:   { label: 'Flagged',   color: '#dc2626' },
          map:       { label: 'Map View',  color: '#0ea5e9' },
          stats:     { label: 'Stats',     color: '#a855f7' },
          dashboard: { label: 'Dashboard', color: '#0ea5e9' },
        };
        const chips = [];
        if (tab !== 'all') {
          const meta = TAB_META[tab];
          if (meta) chips.push({ label: `View: ${meta.label}`, color: meta.color, onRemove: () => setTab('all') });
        }
        if (categoryFilter) {
          chips.push({ label: `Cat: ${CATEGORY_LABEL[categoryFilter] || categoryFilter}`, color: '#16a34a', onRemove: () => setCategoryFilter('') });
        }
        if (!chips.length) return null;
        return (
          <FilterChipBar
            chips={chips}
            hint={tab !== 'all' ? '× to return to All Records' : undefined}
            onClearAll={chips.length > 1 ? () => { setTab('all'); setCategoryFilter(''); } : undefined}
          />
        );
      })()}

      {/* ── Tabs ── */}
      <div style={S.tabs}>
        {[
          { key: 'all',       label: `All (${allRecords.length})`   },
          { key: 'flagged',   label: `🚩 Flagged (${flagged.length})` },
          { key: 'map',       label: '🗺️ Map View' },
          { key: 'stats',     label: '📊 Stats' },
          { key: 'dashboard', label: '📋 Dashboard' },
        ].map((t) => (
          <button key={t.key} style={S.tab(tab === t.key)} onClick={() => setTab(t.key)}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── All records ── */}
      {tab === 'all' && (
        allRecords.length === 0
          ? <p style={{ color: '#6b7280' }}>No records yet.</p>
          : allRecords.map((r) => (
              <RecordCard
                key={r._id}
                record={r}
                canReview
                onSetFlags={handleSetFlags}
                onAddNote={handleAddNote}
                onDelete={handleDelete}
              />
            ))
      )}

      {/* ── Flagged records ── */}
      {tab === 'flagged' && (
        flagged.length === 0
          ? <p style={{ color: '#6b7280' }}>No flagged records — nothing needs attention. 🎉</p>
          : flagged.map((r) => (
              <RecordCard
                key={r._id}
                record={r}
                canReview
                onSetFlags={handleSetFlags}
                onAddNote={handleAddNote}
                onDelete={handleDelete}
              />
            ))
      )}

      {/* ── Map view ── */}
      {tab === 'map' && (() => {
        const dataLayers = Object.entries(
          allRecords.reduce((acc, r) => {
            if (!r.location?.coordinates) return acc;
            (acc[r.category] = acc[r.category] || []).push(r);
            return acc;
          }, {})
        ).map(([cat, data]) => ({ dataTypeId: cat, data }));
        return dataLayers.length > 0
          ? <MapEngine dataLayers={dataLayers} height="540px" onFeatureClick={null} harvestTenantId={tenantId} />
          : <p style={{ color: '#6b7280' }}>No geotagged records to display on the map.</p>;
      })()}

      {/* ── Stats view ── */}
      {tab === 'stats' && stats && (
        <div>
          <h3 style={{ fontWeight: 700, marginBottom: 12 }}>By Category</h3>
          <StatGrid items={stats.byCategory} />
          <h3 style={{ fontWeight: 700, margin: '20px 0 12px' }}>By Season</h3>
          <StatGrid items={stats.bySeason} />
          <h3 style={{ fontWeight: 700, margin: '20px 0 12px' }}>Recent submissions</h3>
          {stats.recentRecords?.map((r) => (
            <div key={r._id} style={{ ...S.card, fontSize: 14 }}>
              {CATEGORY_LABEL[r.category] || r.category}
              {r.species ? ` — ${r.species}` : ''}
              <span style={{ color: '#6b7280', marginLeft: 8 }}>
                by {r.userId?.name || 'Unknown'} · {new Date(r.createdAt).toLocaleDateString()}
              </span>
            </div>
          ))}
        </div>
      )}

      {/* ── Dashboard view ── */}
      {tab === 'dashboard' && (() => {
        // Apply date filter to all records using harvestDate
        let dash = [...records];
        if (dashDateFrom) {
          const from = new Date(dashDateFrom);
          dash = dash.filter(r => new Date(r.harvestDate) >= from);
        }
        if (dashDateTo) {
          const to = new Date(dashDateTo);
          to.setHours(23, 59, 59, 999);
          dash = dash.filter(r => new Date(r.harvestDate) <= to);
        }
        const hasFilter = !!(dashDateFrom || dashDateTo);

        const dTotal   = dash.length;
        const dFlagged = dash.filter(r => r.flags?.length > 0).length;

        // Category breakdown
        const catKeys = Object.keys(CATEGORY_LABEL);
        const catTally = catKeys.map(k => ({
          key: k, label: CATEGORY_LABEL[k].replace(/^.*?\s/, ''),
          count: dash.filter(r => r.category === k).length,
        })).filter(c => c.count > 0).sort((a, b) => b.count - a.count);

        // Season breakdown
        const seasonColors = { spring:'#22c55e', summer:'#f59e0b', fall:'#f97316', winter:'#0ea5e9' };
        const seasonTally = Object.entries(SEASON_LABEL).map(([k, label]) => ({
          key: k, label: label.replace(/^.*?\s/, ''), color: seasonColors[k] || '#64748b',
          count: dash.filter(r => r.season === k).length,
        })).filter(s => s.count > 0);

        // Flag breakdown
        const flagTally = FLAG_TYPES.map(k => ({
          key: k, label: FLAG_META[k].label, color: FLAG_META[k].color,
          count: dash.filter(r => r.flags?.includes(k)).length,
        })).filter(f => f.count > 0);

        // Flagged records (attention queue), most recently touched first
        const attentionQ = [...dash.filter(r => r.flags?.length > 0)]
          .sort((a, b) => new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt))
          .slice(0, 8);

        // Exports
        const buildHarvestMD = () => {
          const now = new Date();
          const lines = [`# Harvest Records — Staff Summary`,'',
            `**Generated:** ${now.toLocaleDateString('en-GB')} ${now.toLocaleTimeString('en-GB',{hour:'2-digit',minute:'2-digit'})}`,
            `**Date filter:** ${(dashDateFrom||dashDateTo) ? `${dashDateFrom||'start'} → ${dashDateTo||'now'}` : 'All dates'}`,
            `**Records in view:** ${dTotal}`,
            '', '---', '',
            '## Overview', '',
            '| Metric | Count |', '|--------|-------|',
            `| Total Records | ${dTotal} |`,
            `| Flagged | ${dFlagged} |`,
            '', '---', '', '## By Category', '',
            '| Category | Count |', '|----------|-------|',
            ...catTally.map(c => `| ${CATEGORY_LABEL[c.key] || c.label} | ${c.count} |`),
            '', '---', '', '## By Season', '',
            '| Season | Count |', '|--------|-------|',
            ...seasonTally.map(s => `| ${SEASON_LABEL[s.key] || s.label} | ${s.count} |`),
          ];
          if (flagTally.length) {
            lines.push('', '---', '', '## By Flag', '',
              '| Flag | Count |', '|------|-------|',
              ...flagTally.map(f => `| ${f.label} | ${f.count} |`));
          }
          if (attentionQ.length) {
            lines.push('', '---', '', `## Flagged — Needs Attention (${attentionQ.length})`, '');
            attentionQ.forEach(r => {
              lines.push(`- ${CATEGORY_LABEL[r.category] || r.category}${r.species ? ` — ${r.species}` : ''} · harvested ${new Date(r.harvestDate).toLocaleDateString()} · flags: ${(r.flags || []).join(', ')}`);
            });
          }
          lines.push('', '---', '', '_CIP harvesting staff export._');
          return lines.join('\n');
        };

        const buildHarvestCSV = () => {
          const esc = v => `"${String(v??'').replace(/"/g,'""')}"`;
          const hdr = ['Category','Species','Season','Harvest Date','Flags','Contributor','Submitted','Observations','Kept Quantity','Quantity Unit','Caught / Harvested Quantity','Outcome','Size','Size Unit','Sex','Condition'];
          const rows = dash.map(r => [
            esc(CATEGORY_LABEL[r.category] || r.category),
            esc(r.species || ''),
            esc(SEASON_LABEL[r.season] || r.season || ''),
            esc(r.harvestDate ? new Date(r.harvestDate).toLocaleDateString('en-GB') : ''),
            esc((r.flags || []).join('; ')),
            esc(r.userId?.name || ''),
            esc(r.createdAt ? new Date(r.createdAt).toLocaleDateString('en-GB') : ''),
            esc(r.observations || ''),
            esc(r.quantity),
            esc(r.unit),
            esc(r.harvestDetails?.caughtQuantity),
            esc(r.harvestDetails?.resourceOutcome),
            esc(r.harvestDetails?.sizeValue),
            esc(r.harvestDetails?.sizeUnit),
            esc(r.harvestDetails?.sex),
            esc(r.harvestDetails?.condition),
          ].join(','));
          return [hdr.join(','), ...rows].join('\r\n');
        };

        return (
          <div style={{ fontSize:13, color:'#e2e8f0' }}>
            {/* Toolbar */}
            <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap', marginBottom:14 }}>
              <span style={{ fontSize:10, color:'#475569', fontWeight:700, letterSpacing:1 }}>FROM</span>
              <input type="date" value={dashDateFrom} onChange={e => setDashFrom(e.target.value)}
                style={DB_STYLE.dateInput} />
              <span style={{ fontSize:10, color:'#475569', fontWeight:700, letterSpacing:1 }}>TO</span>
              <input type="date" value={dashDateTo} onChange={e => setDashTo(e.target.value)}
                style={DB_STYLE.dateInput} />
              <div style={{ flex:1 }} />
              <button onClick={() => downloadFile(buildHarvestMD(), 'harvest-summary.md', 'text/markdown;charset=utf-8')}
                style={DB_STYLE.btnMd}>
                📥 Export .md
              </button>
              <button onClick={() => downloadFile(buildHarvestCSV(), 'harvest-records.csv', 'text/csv;charset=utf-8')}
                style={DB_STYLE.btnCsv}>
                📊 Export .csv
              </button>
            </div>

            {/* Date filter chips */}
            {hasFilter && (
              <FilterChipBar
                chips={[
                  dashDateFrom && { label: `From: ${dashDateFrom}`, color: '#0ea5e9', onRemove: () => setDashFrom('') },
                  dashDateTo   && { label: `To: ${dashDateTo}`,     color: '#0ea5e9', onRemove: () => setDashTo('') },
                ].filter(Boolean)}
                onClearAll={dashDateFrom && dashDateTo ? () => { setDashFrom(''); setDashTo(''); } : undefined}
              />
            )}

            {/* Stat tiles */}
            <div style={{ display:'flex', gap:10, flexWrap:'wrap', marginBottom:4 }}>
              <DashTile label={`Records${hasFilter ? ' (filtered)' : ''}`} value={dTotal} color="#64748b" />
              <DashTile label="Flagged" value={dFlagged} color={dFlagged > 0 ? '#dc2626' : '#334155'} />
            </div>

            {/* Category breakdown */}
            <DashSection label="BY CATEGORY" />
            {catTally.length > 0 ? <DashSegBar items={catTally} total={dTotal} />
              : <div style={{ fontSize:12, color:'#334155', fontStyle:'italic' }}>No records.</div>}

            {/* Season breakdown */}
            <DashSection label="BY SEASON" />
            {seasonTally.length > 0 ? <DashSegBar items={seasonTally} total={dTotal} />
              : <div style={{ fontSize:12, color:'#334155', fontStyle:'italic' }}>No records.</div>}

            {/* Flag breakdown */}
            {flagTally.length > 0 && (
              <>
                <DashSection label="BY FLAG" />
                <DashSegBar items={flagTally} total={dTotal} />
              </>
            )}

            {/* Flagged records (attention queue) */}
            {attentionQ.length > 0 && (
              <>
                <DashSection label={`FLAGGED — NEEDS ATTENTION (${attentionQ.length})`} />
                <div style={{ display:'flex', flexDirection:'column', gap:5 }}>
                  {attentionQ.map(r => (
                    <div key={r._id} style={{ background:'#0f172a', borderRadius:6, padding:'8px 12px',
                      borderLeft:'3px solid #dc2626', display:'flex', gap:10, alignItems:'center', flexWrap:'wrap' }}>
                      <span style={{ fontSize:12, color:'#cbd5e1', flex:1 }}>
                        {CATEGORY_LABEL[r.category] || r.category}
                        {r.species ? ` — ${r.species}` : ''}
                      </span>
                      <span style={{ fontSize:10, color:'#64748b' }}>
                        {(r.flags || []).map(f => FLAG_META[f]?.label || f).join(', ')}
                      </span>
                      <span style={{ fontSize:10, color:'#475569' }}>
                        by {r.userId?.name || 'unknown'}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}
            {attentionQ.length === 0 && (
              <div style={{ marginTop:14, fontSize:13, color:'#22c55e', fontStyle:'italic' }}>
                ✓ No flagged records in the selected period.
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
}

// ─── ContributorView ──────────────────────────────────────────────────────────
function ContributorView({
  tenantId, initialTab = 'submit', basicMode = false, assistedMode = false, canCreateSpecies = false,
}) {
  const normalizedInitialTab = ['submit', 'history', 'map'].includes(initialTab) ? initialTab : 'submit';
  const [tab, setTab]         = useState(normalizedInitialTab);
  const [showMyPoints, setShowMyPoints] = useState(true);
  const [mapControlsContainer, setMapControlsContainer] = useState(null);
  const [editingRecord, setEditingRecord] = useState(null);
  const [placingPoint, setPlacingPoint] = useState(false);
  const [pointDetailsOpen, setPointDetailsOpen] = useState(false);
  const [logIntent, setLogIntent] = useState(null); // null | 'harvest' | 'count' | 'environmental'
  const [myRecords, setMyRec] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubm] = useState(false);
  const [error, setError]     = useState('');
  const [success, setSuccess] = useState('');
  const [mapPickPoint, setMapPickPoint] = useState(null);
  const [draftResource, setDraftResource] = useState({ species: '', speciesId: '', harvestCategory: '' });
  const [guidedDraft, setGuidedDraft] = useState(null);
  const [assistedParticipant, setAssistedParticipant] = useState(null);
  const [assistedConsent, setAssistedConsent] = useState(false);
  const [harvestConfig, setHarvestConfig] = useState(null);
  const [stewardshipData, setStewardshipData] = useState([]);
  const [stewardshipSpeciesName, setStewardshipSpeciesName] = useState('');
  const [guidanceLoading, setGuidanceLoading] = useState(false);
  const [guidanceError, setGuidanceError] = useState('');
  const [guidanceRevision, setGuidanceRevision] = useState(0);
  const [selectedSpeciesId, setSelectedSpeciesId] = useState('');
  const [showStewardship, setShowStewardship] = useState(true);
  const [showRestrictions, setShowRestrictions] = useState(true);
  const [pointGeography, setPointGeography] = useState([]);
  const [pointGeographyLoading, setPointGeographyLoading] = useState(false);
  const [referenceCatalog, setReferenceCatalog] = useState(null);
  const [referenceData, setReferenceData] = useState(null);
  const [hiddenReferenceIds, setHiddenReferenceIds] = useState([]);
  const [referenceRevision, setReferenceRevision] = useState(0);
  const hasLayerRules = !!harvestConfig?.layerRules?.length;
  const referenceLayers = useMemo(() => configuredManagementLayers(
    referenceCatalog?.tenantId === tenantId ? referenceCatalog.layers : [],
    draftResource.harvestCategory, draftResource.species, harvestConfig?.layerRules || [],
  ), [referenceCatalog, tenantId, draftResource.harvestCategory, draftResource.species, harvestConfig]);
  const enabledReferenceLayers = useMemo(() => referenceLayers.filter((layer) =>
    !hiddenReferenceIds.includes(String(layer._id))), [referenceLayers, hiddenReferenceIds]);

  useEffect(() => {
    if (!basicMode || !tenantId || !hasLayerRules) return undefined;
    let current = true;
    setReferenceCatalog({ tenantId, layers: [], loading: true });
    getAvailableGeographyLayers({ tenantId, module: 'harvesting' })
      .then((response) => {
        if (current) setReferenceCatalog({ tenantId, layers: response.data, loading: false });
      })
      .catch((error) => {
        if (current) setReferenceCatalog({ tenantId, layers: [], loading: false,
          error: error.response?.data?.message || 'Could not load configured reference layers.' });
      });
    return () => { current = false; };
  }, [basicMode, tenantId, hasLayerRules, referenceRevision]);

  useEffect(() => {
    if (!basicMode || !tenantId) return undefined;
    let current = true;
    setReferenceData(null);
    if (!enabledReferenceLayers.length) return undefined;
    Promise.all(enabledReferenceLayers.map(async (layer) => {
      if (isLakesLayer(layer)) return { layer };
      try {
        const response = await getManagedLayerGeoJson(layer._id, { tenantId, module: 'harvesting', limit: 3000 });
        return { layer, data: response.data };
      } catch (error) {
        return { layer, error: `${layer.displayName}: ${error.response?.data?.message || 'Could not load shapes.'}` };
      }
    })).then((entries) => {
      if (current) setReferenceData({ layers: enabledReferenceLayers, entries });
    });
    return () => { current = false; };
  }, [basicMode, tenantId, enabledReferenceLayers, referenceRevision]);
  const visibleReferenceEntries = referenceData?.layers === enabledReferenceLayers ? referenceData.entries : [];

  const loadMyRecords = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await getMyRecords(tenantId, { limit: 100 });
      const records = [...res.data.records];
      if (basicMode) {
        for (let page = 2; page <= (res.data.pages || 1); page += 1) {
          const next = await getMyRecords(tenantId, { limit: 100, page });
          records.push(...next.data.records);
        }
      }
      setMyRec(records);
    } catch (e) {
      setError(e.response?.data?.message || 'Failed to load your records');
    } finally {
      setLoading(false);
    }
  }, [tenantId, basicMode]);

  useEffect(() => {
    if (basicMode || tab === 'history' || tab === 'map') loadMyRecords();
  }, [basicMode, tab, loadMyRecords]);

  useEffect(() => {
    let alive = true;
    getHarvestConfig(tenantId)
      .then((res) => { if (alive) setHarvestConfig(res.data); })
      .catch((error) => {
        if (alive) {
          setHarvestConfig(null);
          setError(error.response?.data?.message || 'Unable to load harvest setup. Refresh the page to try again.');
        }
      });
    return () => { alive = false; };
  }, [tenantId]);

  useEffect(() => {
    if (normalizedInitialTab !== tab) {
      setTab(normalizedInitialTab);
    }
  }, [basicMode, normalizedInitialTab, tab]);

  const handleSubmit = async (formData) => {
    setSubm(true);
    setError('');
    setSuccess('');
    try {
      const payload = basicMode && mapPickPoint
        ? { ...formData, latitude: mapPickPoint.lat, longitude: mapPickPoint.lng }
        : formData;
      if (assistedMode && (!assistedParticipant?._id || !assistedConsent)) {
        throw new Error('Select a participant and confirm their consent before saving.');
      }
      if (assistedMode && payload.catches) {
        await submitAssistedGuidedHarvest(tenantId, {
          ...payload,
          participantId: assistedParticipant._id,
          consentConfirmed: assistedConsent,
        });
      } else if (basicMode && payload.catches) await submitGuidedHarvest(tenantId, payload);
      else await submitRecord(tenantId, payload);
      setSuccess(assistedMode
        ? `Harvest recorded for ${assistedParticipant.name}. Your staff entry is audited separately from the participant’s ownership.`
        : 'Your harvest record has been submitted. Thank you for contributing!');
      if (!basicMode) setTab('history');
      await loadMyRecords();
      setLogIntent(null);
      if (basicMode) {
        setMapPickPoint(null);
        setPointDetailsOpen(false);
        setPlacingPoint(false);
        setDraftResource({ species: '', speciesId: '', harvestCategory: '' });
        setGuidedDraft(null);
        setStewardshipData([]);
        setStewardshipSpeciesName('');
        setSelectedSpeciesId('');
      }
    } catch (e) {
      setError(e.response?.data?.message || 'Submission failed. Please try again.');
    } finally {
      setSubm(false);
    }
  };

  const handleDelete = async (id) => {
    await deleteRecord(tenantId, id);
    await loadMyRecords();
  };

  const mappedRecords = myRecords.filter((r) => r.location?.coordinates?.length === 2);

  const handleInlineMapPick = (lat, lng) => {
    if (!placingPoint) return;
    setMapPickPoint({ lat, lng, ts: Date.now() });
    setPointDetailsOpen(true);
    setPlacingPoint(false);
    setPointGeography([]);
    setPointGeographyLoading(true);
    lookupGeographyPoint({ lng, lat, tenantId, module: 'harvesting' })
      .then((response) => setPointGeography(response.data || []))
      .catch(() => setPointGeography([]))
      .finally(() => setPointGeographyLoading(false));
  };

  const startPointPlacement = () => {
    if (!draftResource.species.trim() || !draftResource.harvestCategory) return;
    setMapPickPoint(null);
    setPointDetailsOpen(false);
    setPlacingPoint(true);
  };

  const discardDraft = () => {
    setGuidedDraft(null);
    setMapPickPoint(null);
    setPointDetailsOpen(false);
    setPlacingPoint(false);
    setDraftResource({ species: '', speciesId: '', harvestCategory: '' });
    setStewardshipData([]);
    setStewardshipSpeciesName('');
    setSelectedSpeciesId('');
    setPointGeography([]);
  };

  const closePointDetails = () => setPointDetailsOpen(false);

  const handleSpeciesForStewardship = useCallback((speciesId, speciesName) => {
    if (!speciesId) {
      setStewardshipData([]);
      setStewardshipSpeciesName('');
      setSelectedSpeciesId('');
      return;
    }
    setSelectedSpeciesId(String(speciesId));
    setStewardshipSpeciesName(speciesName || '');
  }, []);

  useEffect(() => {
    let current = true;
    setStewardshipData([]);
    setGuidanceError('');
    setGuidanceLoading(false);
    if (!selectedSpeciesId || !tenantId) return undefined;
    setGuidanceLoading(true);
    getPolygonsForSpecies(selectedSpeciesId, tenantId)
      .then((response) => { if (current) setStewardshipData(response.data || []); })
      .catch((requestError) => {
        if (current) setGuidanceError(requestError.response?.data?.message || 'Could not load published species guidance. Use Retry guidance to try again.');
      })
      .finally(() => { if (current) setGuidanceLoading(false); });
    return () => { current = false; };
  }, [selectedSpeciesId, tenantId, guidanceRevision]);

  const handleDraftResourceSelect = useCallback(({ speciesId, speciesName, harvestCategory, speciesCategory }) => {
    const category = resolveHarvestCategory({ speciesId, speciesName, harvestCategory, speciesCategory }, harvestConfig);
    setDraftResource({
      species: speciesName || '',
      speciesId: speciesId || '',
      harvestCategory: category,
    });
    setGuidedDraft(null);
    if (harvestConfig) setError('');
    handleSpeciesForStewardship(speciesId, speciesName);
  }, [handleSpeciesForStewardship, harvestConfig]);

  if (basicMode) {
    return (
      <div className="harvest-map-flow">
        {assistedMode && (
          <AssistedParticipantPanel
            tenantId={tenantId}
            onSelectionChange={(participant, consentConfirmed) => {
              setAssistedParticipant(participant);
              setAssistedConsent(consentConfirmed);
            }}
          />
        )}
        {error && !pointDetailsOpen && <div role="alert" style={S.err}>{error}</div>}
        {success && (
          <div style={{
            background: '#f0fdf4', border: '1px solid #bbf7d0',
            borderRadius: 6, padding: '10px 14px', color: '#166534',
            fontSize: 13, marginBottom: 12,
          }}>
            ✅ {success}
          </div>
        )}

        <section className="harvest-map-flow__resource">
          <h3>Log a harvest</h3>
          <label htmlFor="harvest-resource-picker">Step 1 of 4 — Select species or resource</label>
          <SpeciesAutocomplete
            id="harvest-resource-picker"
            canCreate={canCreateSpecies}
            disabled={!harvestConfig || submitting}
            value={draftResource.species}
            onSelect={handleDraftResourceSelect}
            onInputChange={(speciesName) => handleDraftResourceSelect({ speciesName })}
            popularSpecies={harvestConfig?.popularSpecies || []}
            placeholder="Choose what you harvested…"
          />
          <p>Published guidance is not a guarantee of safety or harvesting permission.</p>
          {draftResource.species && !draftResource.speciesId && (
            <p>Select a matching species from the catalogue results to continue. Typing a name does not add a new species.</p>
          )}
          {draftResource.speciesId && !draftResource.harvestCategory && (
            <label>
              This resource has no harvest category yet. Choose its category to continue:
              <select value="" onChange={(event) => setDraftResource((current) => ({ ...current, harvestCategory: event.target.value }))}>
                <option value="">Choose category</option>
                {(harvestConfig?.categories || []).map((category) => <option key={category.key} value={category.key}>{category.label}</option>)}
              </select>
            </label>
          )}
          {draftResource.harvestCategory && <p>Automatically selected: {harvestConfig?.categories?.find((category) => category.key === draftResource.harvestCategory)?.label || draftResource.harvestCategory}</p>}
        </section>

        <div className="harvest-map-flow__actions">
          <button
            type="button"
            onClick={mapPickPoint ? () => setPointDetailsOpen(true) : startPointPlacement}
            disabled={!draftResource.speciesId || !draftResource.harvestCategory
              || (assistedMode && (!assistedParticipant?._id || !assistedConsent))}
            className="harvest-map-flow__add"
          >
            {mapPickPoint ? 'Step 3 — Complete harvest details' : 'Step 2 — Choose location using the map layers'}
          </button>
          {mapPickPoint && (
            <button type="button" onClick={discardDraft} className="harvest-map-flow__discard">
              Discard draft
            </button>
          )}
        </div>

        {placingPoint && (
          <div className="harvest-map-flow__instruction">
            <span>Tap the map where you want to add this point.</span>
            <button type="button" onClick={() => setPlacingPoint(false)}>Cancel placement</button>
          </div>
        )}

        {referenceCatalog?.tenantId === tenantId && referenceCatalog.error && (
          <p role="alert">{referenceCatalog.error} <button type="button" onClick={() => setReferenceRevision((value) => value + 1)}>Retry reference layers</button></p>
        )}
        {enabledReferenceLayers.length > 0 && referenceData?.layers !== enabledReferenceLayers && <p role="status">Loading reference layer shapes...</p>}
        {visibleReferenceEntries.filter((entry) => entry.error).map((entry) => (
          <p key={entry.layer._id} role="alert">{entry.error} <button type="button" onClick={() => setReferenceRevision((value) => value + 1)}>Retry reference layers</button></p>
        ))}
        {visibleReferenceEntries.filter((entry) => entry.data?.truncated).map((entry) => (
          <p key={entry.layer._id} role="status">{entry.layer.displayName}: only the first 3,000 shapes are displayed. This is not the complete layer.</p>
        ))}
        {guidanceLoading && <p role="status">Loading published areas for this species...</p>}
        {guidanceError && <p role="alert">{guidanceError} <button type="button" onClick={() => setGuidanceRevision((value) => value + 1)}>Retry guidance</button></p>}
        {selectedSpeciesId && !guidanceLoading && !guidanceError && !stewardshipData.length && (
          <p>No published guidance areas for this species yet. An unmarked area is not confirmation that harvesting is safe or permitted.</p>
        )}

        <details className="harvest-map-flow__map-options">
          <summary>Map layers &amp; guidance</summary>
          <div ref={setMapControlsContainer} />
          <button type="button" className={`harvest-map-flow__layers-pill managed${showMyPoints ? ' active' : ''}`}
            aria-pressed={showMyPoints} onClick={() => setShowMyPoints((value) => !value)}>My harvest points</button>
          {loading && <p role="status">Loading your harvest points...</p>}
          <MapLayerControls
            guidanceOnly
            selectedSpeciesId={selectedSpeciesId}
            selectedSpeciesName={draftResource.species}
            selectedCategory={draftResource.harvestCategory}
            layerRules={harvestConfig?.layerRules || []}
            managedLayers={referenceLayers}
            activeManagedLayerIds={enabledReferenceLayers.map((layer) => layer._id)}
            onToggleManagedLayer={(layer) => setHiddenReferenceIds((ids) => ids.includes(String(layer._id))
              ? ids.filter((id) => id !== String(layer._id)) : [...ids, String(layer._id)])}
            managedLayersLoading={referenceCatalog?.loading}
          />
          <p>Only approved, published areas assigned to your chosen species appear. Positive and negative scores inform planning; they do not establish availability or legal harvesting permission. Territory visibility is independent. Select one of your harvest points to see its details or edit it without leaving the map.</p>
        </details>

        <HarvestMap
          tenantId={tenantId}
          records={showMyPoints ? mappedRecords : []}
          onEditRecord={setEditingRecord}
          readonly
          pickMode={placingPoint}
          onMapSelect={handleInlineMapPick}
          selectedPoint={mapPickPoint}
          stewardshipAssignments={stewardshipData}
          stewardshipSpeciesName={stewardshipSpeciesName}
          selectedSpeciesId={selectedSpeciesId}
          showStewardship={showStewardship}
          showRestrictions={showRestrictions}
          onToggleStewardship={() => setShowStewardship((value) => !value)}
          onToggleRestrictions={() => setShowRestrictions((value) => !value)}
          controlsContainer={mapControlsContainer}
          focusGuidance={!placingPoint && !mapPickPoint}
          managedGeographyLayers={visibleReferenceEntries.filter((entry) => !entry.error)}
          height="clamp(340px, 64dvh, 760px)"
        />

        {editingRecord && <HarvestRecordEditDialog tenantId={tenantId} record={editingRecord}
          onCancel={() => setEditingRecord(null)}
          onSaved={(updatedRecord) => {
            setMyRec((records) => records.map((record) => record._id === updatedRecord._id ? updatedRecord : record));
            setEditingRecord(null);
            setSuccess('Harvest record updated.');
          }} />}

        {pointDetailsOpen && mapPickPoint && (
          <div className="harvest-point-modal" role="dialog" aria-modal="true" aria-label="Complete harvest point details">
            <div className="harvest-point-modal__panel">
              <div className="harvest-point-modal__header">
                <div>
                  <h3>Log your harvest</h3>
                  <p>Location selected: {mapPickPoint.lat.toFixed(4)}, {mapPickPoint.lng.toFixed(4)}</p>
                  {pointGeographyLoading && <p>Checking management areas…</p>}
                  {!pointGeographyLoading && pointGeography.length > 0 && (
                    <div className="harvest-point-modal__geography">
                      {pointGeography.map((match) => (
                        <span key={`${match.layerId}-${match.featureId}`}>
                          {match.layerName}: {match.featureName}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
                <button type="button" disabled={submitting} onClick={closePointDetails} aria-label="Close point details">×</button>
              </div>
              <GuidedHarvestForm
                onSubmit={handleSubmit}
                loading={submitting}
                error={error}
                assistedMode={assistedMode}
                canCreateSpecies={canCreateSpecies}
                taxonomy={harvestConfig}
                resource={draftResource}
                draft={guidedDraft}
                onDraftChange={setGuidedDraft}
                onBackSpecies={() => {
                  setPointDetailsOpen(false);
                }}
                onBackLocation={startPointPlacement}
              />
              <button type="button" disabled={submitting} className="harvest-point-modal__cancel" onClick={closePointDetails}>Close</button>
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div>
      <div style={S.tabs}>
        {[
          { key: 'submit',  label: '📝 Log Harvest' },
          { key: 'history', label: `My Records (${myRecords.length || '…'})` },
          { key: 'map',     label: '🗺️ My Map' },
        ].map((t) => (
          <button key={t.key} style={S.tab(tab === t.key)} onClick={() => setTab(t.key)}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── Log Harvest ── */}
      {tab === 'submit' && (
        <div>
          {success && (
            <div style={{
              background: '#f0fdf4', border: '1px solid #bbf7d0',
              borderRadius: 6, padding: '12px 16px', color: '#166534',
              fontSize: 14, marginBottom: 16,
            }}>
              ✅ {success}
            </div>
          )}
          {!logIntent && (
            <div>
              <p style={{ fontSize: 15, color: '#374151', marginBottom: 16 }}>
                What would you like to record today?
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(190px, 1fr))', gap: 12, marginBottom: 10 }}>
                {[
                  { key: 'harvest', icon: '🌿', title: 'Log a Harvest', desc: 'Record fish, game, berries, plants, or anything you harvested.', color: '#166534', bg: '#f0fdf4', border: '#bbf7d0' },
                  { key: 'count', icon: '📊', title: 'Species Count', desc: 'Count or report what you observed. Every sighting helps track population health.', color: '#1d4ed8', bg: '#eff6ff', border: '#bfdbfe' },
                  { key: 'environmental', icon: '⚠️', title: 'Environmental Report', desc: 'Report pollution, habitat damage, water quality issues, or other concerns.', color: '#b45309', bg: '#fffbeb', border: '#fde68a' },
                ].map((opt) => (
                  <button
                    key={opt.key}
                    type="button"
                    onClick={() => setLogIntent(opt.key)}
                    style={{ background: opt.bg, border: `2px solid ${opt.border}`, borderRadius: 10, padding: '18px 16px', textAlign: 'left', cursor: 'pointer', transition: 'box-shadow .15s' }}
                    onMouseEnter={(e) => { e.currentTarget.style.boxShadow = '0 4px 14px rgba(0,0,0,.10)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.boxShadow = 'none'; }}
                  >
                    <div style={{ fontSize: 26, marginBottom: 6 }}>{opt.icon}</div>
                    <div style={{ fontWeight: 700, fontSize: 14, color: opt.color, marginBottom: 4 }}>{opt.title}</div>
                    <div style={{ fontSize: 12, color: '#6b7280', lineHeight: 1.5 }}>{opt.desc}</div>
                  </button>
                ))}
              </div>
              <p style={{ fontSize: 11, color: '#9ca3af', marginTop: 6 }}>
                All submissions are yours — you can edit or remove them at any time.
              </p>
            </div>
          )}
          {logIntent && (
            <div>
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: logIntent === 'environmental' ? '#fffbeb' : logIntent === 'count' ? '#eff6ff' : '#f0fdf4', border: `1px solid ${logIntent === 'environmental' ? '#fde68a' : logIntent === 'count' ? '#bfdbfe' : '#bbf7d0'}`, borderRadius: 8, padding: '10px 14px', marginBottom: 16, flexWrap: 'wrap', gap: 8 }}>
                <span style={{ fontSize: 14, fontWeight: 600, color: '#374151' }}>
                  {logIntent === 'harvest' && '🌿 Logging a Harvest'}
                  {logIntent === 'count' && '📊 Species Count / Observation'}
                  {logIntent === 'environmental' && '⚠️ Environmental Report'}
                </span>
                <button type="button" onClick={() => setLogIntent(null)} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: 13, color: '#6b7280', textDecoration: 'underline', padding: 0 }}>
                  ← Change
                </button>
              </div>
              <HarvestForm
                tenantId={tenantId}
                onSubmit={handleSubmit}
                loading={submitting}
                error={error}
                taxonomy={harvestConfig}
                initial={logIntent === 'environmental' ? { category: 'environmental' } : {}}
              />
            </div>
          )}
        </div>
      )}

      {/* ── My Records history ── */}
      {tab === 'history' && (
        loading
          ? <p>Loading…</p>
          : myRecords.length === 0
            ? (
              <div style={{ textAlign: 'center', padding: '40px 0', color: '#6b7280' }}>
                <p style={{ fontSize: 32 }}>🌿</p>
                <p>No records yet. Start by submitting your first harvest record!</p>
                <button style={{ ...S.btn('#16a34a'), marginTop: 8 }} onClick={() => setTab('submit')}>
                  Submit a Record
                </button>
              </div>
            )
            : myRecords.map((r) => (
                <RecordCard
                  key={r._id}
                  record={r}
                  isOwn
                  onDelete={handleDelete}
                />
              ))
      )}

      {/* ── My Map ── */}
      {tab === 'map' && (
        loading
          ? <p>Loading…</p>
          : mappedRecords.length === 0
            ? <p style={{ color: '#6b7280' }}>None of your records have coordinates yet.</p>
            : <HarvestMap tenantId={tenantId} records={mappedRecords} readonly />
      )}
    </div>
  );
}

// ─── HarvestingPage ───────────────────────────────────────────────────────────
// mode prop: 'contribute' | 'review' | 'oversight' | undefined (auto-derived)
export default function HarvestingPage({ mode }) {
  const { activeTenant, user } = useAuth();
  const tenantCtx = useTenantSafe();
  const location = useLocation();

  const qs = new URLSearchParams(location.search);
  const isEmbedded = qs.get('embedded') === '1';
  const rsminReturnUrl = qs.get('rsminReturn');
  const rsminReturnLabel = qs.get('rsminLabel') || 'Back to RSMin';

  // If rendered inside TenantShell (/t/:slug/...) use the URL-derived tenant,
  // otherwise fall back to the session's single active tenant.
  const tenantId = tenantCtx?.viewTenant?._id || activeTenant?._id;

  const forceContribute = qs.get('mode') === 'contribute';
  const contributorTabFromQuery = ['submit', 'history', 'map'].includes(qs.get('tab'))
    ? qs.get('tab')
    : 'submit';
  const useBasicContributorMode = true;

  const isHarvestingStaff = canStaffSection(user, tenantId, 'harvesting');
  const isStewardshipStaff = canStaffSection(user, tenantId, 'stewardship');
  const isReviewer = !forceContribute && (isHarvestingStaff || isStewardshipStaff);

  // Reviewer-role users can toggle to their own contributor view
  const [showContributor, setShowContributor] = useState(false);
  const [showAssistedIntake, setShowAssistedIntake] = useState(false);
  const activeView = isReviewer && isHarvestingStaff && showAssistedIntake
    ? 'assisted'
    : isReviewer && showContributor ? 'contributor' : isReviewer ? 'reviewer' : 'contributor';
  const pageStyle = isEmbedded ? { ...S.page, maxWidth: '100%', padding: '6px' } : S.page;

  if (!tenantId && !isReviewer) {
    return (
      <div className={`harvesting-page${isEmbedded ? ' harvesting-page--embedded' : ''}`} style={pageStyle}>
        <p style={{ color: '#6b7280' }}>No active community selected.</p>
      </div>
    );
  }

  return (
    <div className={`harvesting-page${isEmbedded ? ' harvesting-page--embedded' : ''}`} style={pageStyle}>
      {!isEmbedded && rsminReturnUrl && (
        <div
          style={{
            marginBottom: 14,
            background: '#ecfeff',
            border: '1px solid #99f6e4',
            borderRadius: 8,
            padding: '10px 12px',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: 10,
            flexWrap: 'wrap',
          }}
        >
          <span style={{ color: '#0f766e', fontSize: 13 }}>
            You are viewing CIP harvesting from RSMin.
          </span>
          <a href={rsminReturnUrl} style={{ color: '#0f766e', fontWeight: 700, textDecoration: 'none' }}>
            {rsminReturnLabel}
          </a>
        </div>
      )}

      {!isEmbedded && (
        <>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: 8, marginBottom: 4 }}>
            <h1 style={{ ...S.heading, margin: 0 }}>🌿 Harvesting</h1>
            {isReviewer && (
              activeView === 'reviewer'
                ? (
                  <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button
                    onClick={() => setShowContributor(true)}
                    style={{
                      padding: '7px 14px', background: '#f0fdf4', color: '#166534',
                      border: '1.5px solid #86efac', borderRadius: 7, fontSize: 13,
                      fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
                    }}
                  >
                    📝 Log My Own Activity
                  </button>
                  {isHarvestingStaff && (
                    <button
                      onClick={() => { setShowContributor(false); setShowAssistedIntake(true); }}
                      style={{
                        padding: '7px 14px', background: '#ecfdf5', color: '#166534',
                        border: '1.5px solid #86efac', borderRadius: 7, fontSize: 13,
                        fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
                      }}
                    >
                      ☎ Assisted phone intake
                    </button>
                  )}
                  </div>
                )
                : (
                  <button
                    onClick={() => { setShowContributor(false); setShowAssistedIntake(false); }}
                    style={{
                      padding: '7px 14px', background: '#f8fafc', color: '#374151',
                      border: '1.5px solid #d1d5db', borderRadius: 7, fontSize: 13,
                      fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
                    }}
                  >
                    ← Data Quality Dashboard
                  </button>
                )
            )}
          </div>
          <details className="harvesting-page__help"><summary>About {activeView === 'reviewer' ? 'data quality' : activeView === 'assisted' ? 'assisted intake' : 'harvest logging'}</summary>
          <p>{activeView === 'reviewer'
            ? 'Data quality: contributors are responsible for their own records; no staff approval is required. Flag unreliable or impaired data and document why it should not be used in downstream pipelines. Use the contributor view to manage your own entries.'
            : activeView === 'assisted'
              ? 'Record the caller as the harvest subject and yourself as the entering staff actor. Confirm consent, species, map point and catch details before saving.'
              : 'Log what you harvested, use published species guidance on the map, and manage your own entries. Staff configure collection forms in Harvest Setup; stewards manage area guidance in Stewardship.'}</p>

          <p style={{ color: '#6b7280', marginBottom: 24, fontSize: 15 }}>
            {activeView === 'reviewer'
              ? 'Monitor harvest data quality, document limitations, and flag records unsuitable for downstream use.'
              : activeView === 'assisted'
                ? 'Participant details are encrypted at rest. Staff never receive a participant password; profile claim and recovery links are one-time manual handovers and are not emailed by CIP.'
                : 'Record and track your harvesting observations. Your contributions support community planning and traditional knowledge preservation.'}
          </p>
          </details>
        </>
      )}

      {activeView === 'reviewer'
        ? <ReviewerView tenantId={tenantId} />
        : <ContributorView
          key={`${tenantId}-${activeView}`}
          tenantId={tenantId}
          initialTab={contributorTabFromQuery}
          basicMode={useBasicContributorMode}
          assistedMode={activeView === 'assisted'}
          canCreateSpecies={isHarvestingStaff}
        />
      }
    </div>
  );
}
